import { useEffect } from "react";

// Adds `.is-visible` to any `.reveal` element when it enters the viewport.
// Bulletproof: uses IntersectionObserver + a scroll/resize fallback + a
// safety timeout, so content can never remain permanently hidden.
export default function useScrollReveal(deps = []) {
  useEffect(() => {
    const revealEl = (el) => el.classList.add("is-visible");

    const scan = () => {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      document.querySelectorAll(".reveal:not(.is-visible)").forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < vh * 0.92 && r.bottom > 0) revealEl(el);
      });
    };

    // 1) Reveal anything already in view right away.
    scan();

    // 2) IntersectionObserver for smooth reveals on scroll.
    let observer;
    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              revealEl(entry.target);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.08, rootMargin: "0px 0px -30px 0px" }
      );
      document.querySelectorAll(".reveal:not(.is-visible)").forEach((el) => observer.observe(el));
    }

    // 3) Scroll/resize fallback in case the observer misses anything.
    window.addEventListener("scroll", scan, { passive: true });
    window.addEventListener("resize", scan);

    // 4) Safety net: reveal everything shortly after load no matter what.
    const t1 = setTimeout(scan, 400);
    const t2 = setTimeout(() => {
      document.querySelectorAll(".reveal:not(.is-visible)").forEach(revealEl);
    }, 1800);

    return () => {
      window.removeEventListener("scroll", scan);
      window.removeEventListener("resize", scan);
      clearTimeout(t1);
      clearTimeout(t2);
      if (observer) observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
